import React from "react";
import { FormGroup } from "semantic-ui-react";
import { type CreditCardInfo, type PaymentFormData } from "../../types";
import { formatCardNumber } from "../../utils/creditCardValidation";
import InputField from "components/forms/InputField/InputField";
import DropdownField from "components/forms/DropdownField";
import Tooltip from "components/common/tooltip/Tooltip";
import type { FormikProps } from "formik";
import FieldLabel from "components/forms/FieldLabel";
import styles from "./CreditCardSection.module.scss";
import { CardIcon } from "features/Licensing/Packs/PaymentModal/components/CardIcon";

interface CreditCardSectionProps {
  creditCardInfo: CreditCardInfo;
  onCardNumberChange: (cardNumber: string) => void;
  formik: FormikProps<PaymentFormData>;
}

const monthOptions = Array.from({ length: 12 }, (_, i) => {
  const month = i + 1;
  const value = month.toString().padStart(2, "0");
  return {
    key: value,
    text: value,
    value,
  };
});

const currentYear = new Date().getFullYear();

const yearOptions = Array.from({ length: 21 }, (_, i) => {
  const year = currentYear + i;
  const shortYear = (year % 100).toString().padStart(2, "0");
  return {
    key: shortYear,
    text: shortYear,
    value: shortYear,
  };
});

const CreditCardSection: React.FC<CreditCardSectionProps> = ({ creditCardInfo, onCardNumberChange, formik }) => {
  const { nameOnCard, cardNumber, expirationMonth, expirationYear, securityCode } = creditCardInfo;

  const handleCardNumberChange = (data: { value: string }) => {
    onCardNumberChange(data.value);
  };

  return (
    <>
      <InputField
        {...formik}
        propertyName={"creditCardInfo.nameOnCard"}
        label={"Name on Card"}
        placeholder={"Enter name on card"}
        value={nameOnCard}
        markAsRequired
      />

      <InputField
        {...formik}
        propertyName={"creditCardInfo.cardNumber"}
        label={"Card Number"}
        placeholder={"Enter card number"}
        value={formatCardNumber(cardNumber)}
        onValueChanged={handleCardNumberChange}
        markAsRequired
        icon={
          <div className={styles.creditCard}>
            <CardIcon cardNumber={cardNumber} />
          </div>
        }
        iconPosition={"left"}
        maxLength={19}
      />

      <FieldLabel markAsRequired label={"Expiration Date"} />
      <FormGroup widths={4}>
        <DropdownField
          {...formik}
          placeholder="Month"
          propertyName={"creditCardInfo.expirationMonth"}
          fluid
          selection
          options={monthOptions}
          value={expirationMonth}
          testID={"expirationMonth"}
        />
        <div className={styles.divider}>/</div>
        <DropdownField
          {...formik}
          placeholder="Year"
          propertyName={"creditCardInfo.expirationYear"}
          fluid
          selection
          options={yearOptions}
          value={expirationYear}
          testID={"expirationYear"}
        />
      </FormGroup>

      <InputField
        {...formik}
        className={styles.securityCode}
        value={securityCode}
        label={"Security Code"}
        propertyName={"creditCardInfo.securityCode"}
        placeholder={"Enter security code"}
        maxLength={4}
        markAsRequired
        adornmentAfter={
          <Tooltip
            content={"The 3 or 4 digit security code on the back of your card"}
            showAlways
            target={"What is this?"}
            className={styles.tooltip}
          />
        }
      />
    </>
  );
};

export default CreditCardSection;
